/* ==========================================================================
   WashWax — Amazon-style product page behaviour
   <wwa-product>  variant buttons, Add to Cart / Buy Now, Frequently bought
                  together, mobile sticky bar
   <wwa-gallery>  thumbnail switching and hover zoom
   <wwa-recs>     "Customers also bought" row, loaded from Shopify
                  product recommendations
   Talks to the Enterprise theme's own cart drawer and cart icon when present.
   ========================================================================== */
(() => {
  'use strict';
  // Both product sections load this file; run it once.
  if (window.__wwaLoaded) return;
  window.__wwaLoaded = true;

  const routes = (window.theme && window.theme.routes) || { cart: '/cart', cartAdd: '/cart/add' };

  /**
   * Formats cents with the shop's money format.
   * @param {number} cents Amount in cents.
   * @param {string} format Shopify money format, for example "${{amount}}".
   * @returns {string} Formatted amount.
   */
  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents) || 0;
    const delimit = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt;
    let out;
    switch (match[1]) {
      case 'amount_no_decimals': out = delimit(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = delimit(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = delimit(value, 0, '.', ','); break;
      case 'amount_with_space_separator': out = delimit(value, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': out = delimit(value, 2, "'", '.'); break;
      default: out = delimit(value, 2, ',', '.');
    }
    return fmt.replace(match[0], out);
  }

  /** Phone layout breakpoint, matching ww-amz.css. */
  const mobileMq = window.matchMedia('(max-width: 749px)');

  /** Returns the theme cart drawer element, if the theme uses one. */
  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  /**
   * Adds items to the cart and refreshes the theme's cart icon and drawer.
   * @param {Array<object>} items Cart items ({ id, quantity, selling_plan? }).
   * @returns {Promise<object>} Shopify's response, including rendered sections.
   */
  async function addToCart(items) {
    const drawer = getCartDrawer();
    const sections = ['cart-icon-bubble'];
    const drawerSection = drawer && drawer.closest('.shopify-section');
    if (drawerSection) sections.push(drawerSection.id.replace('shopify-section-', ''));

    const response = await fetch(`${routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status) {
      let message = data.description || data.message || ((window.wwT && wwT.cart_error) || 'That could not be added to your cart.');
      if (data.errors && typeof data.errors === 'object') {
        message = Object.values(data.errors).flat().join(', ');
      }
      throw new Error(message);
    }
    return data;
  }

  /**
   * Updates the header cart count and opens the drawer after an add.
   * @param {object} data Response from addToCart.
   */
  function refreshCartUi(data) {
    const ProductForm = customElements.get('product-form');
    if (ProductForm && typeof ProductForm.updateCartIcon === 'function' && data.sections) {
      try { ProductForm.updateCartIcon(data); } catch (e) { /* theme API changed; fall through */ }
    }

    const drawer = getCartDrawer();
    if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
      drawer.renderContents(data, true);
    } else {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    }
    document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { source: 'wwa' } }));
  }

  /* ------------------------------------------------------------------------
     <wwa-gallery>
     ------------------------------------------------------------------------ */
  class WwaGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = Array.from(this.querySelectorAll('.wwa-gallery__thumb'));
      this.slides = Array.from(this.querySelectorAll('.wwa-gallery__slide'));
      this.canHover = window.matchMedia('(hover: hover)').matches;

      // Show at most 5 thumbnails; the 5th becomes "+N" and opens the full viewer.
      const MAX_THUMBS = 5;
      const moreIndex = this.thumbs.length > MAX_THUMBS ? MAX_THUMBS - 1 : -1;
      this.thumbs.forEach((thumb, i) => {
        if (moreIndex > -1 && i > moreIndex) {
          const li = thumb.closest('li');
          if (li) li.hidden = true;
          return;
        }
        if (i === moreIndex) {
          thumb.classList.add('is-more');
          thumb.setAttribute('aria-label', `See all ${this.thumbs.length} photos and videos`);
          const badge = document.createElement('span');
          badge.className = 'wwa-gallery__more';
          badge.textContent = `+${this.thumbs.length - moreIndex}`;
          thumb.appendChild(badge);
          thumb.addEventListener('click', () => this.openViewer(i));
          return;
        }
        thumb.addEventListener('click', () => this.show(thumb.dataset.mediaId));
        if (this.canHover) thumb.addEventListener('mouseenter', () => this.show(thumb.dataset.mediaId));
      });

      // Clicking the big photo opens the viewer too (on phones a tap, not a swipe).
      this.querySelectorAll('.wwa-gallery__slide--image').forEach((slide) => {
        slide.addEventListener('click', () => this.openViewer(this.slides.indexOf(slide)));
      });

      if (this.dataset.zoom === 'true' && this.canHover) {
        this.querySelectorAll('.wwa-gallery__zoom').forEach((zoom) => this.bindZoom(zoom));
      }

      // Phone: the stage is a swipe carousel; keep dots and the active slide in step.
      this.stage = this.querySelector('.wwa-gallery__stage');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (this.stage) {
        let ticking = false;
        this.stage.addEventListener('scroll', () => {
          if (ticking || !mobileMq.matches) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            ticking = false;
            const index = Math.round(this.stage.scrollLeft / Math.max(this.stage.clientWidth, 1));
            const slide = this.slides[index];
            if (slide) this.mark(slide.dataset.mediaId);
          });
        }, { passive: true });
      }

      const share = this.querySelector('[data-wwa-share]');
      if (share) share.addEventListener('click', () => this.share(share));
    }

    /**
     * Full-screen viewer with every photo and video, like Amazon's.
     * @param {number} start Index of the item to show first.
     */
    openViewer(start) {
      if (!this.slides.length) return;
      if (!this.viewer) this.buildViewer();
      this.viewerShow(Math.max(0, start || 0));
      if (typeof this.viewer.showModal === 'function') this.viewer.showModal();
      else this.viewer.setAttribute('open', '');
    }

    buildViewer() {
      const dlg = document.createElement('dialog');
      dlg.className = 'wwa-viewer';
      dlg.setAttribute('aria-label', 'Product photos and videos');
      const title = (document.querySelector('.wwa-title') || {}).textContent || '';
      const items = this.slides.map((slide, i) => {
        const thumb = this.thumbs.find((t) => t.dataset.mediaId === slide.dataset.mediaId);
        const timg = thumb && thumb.querySelector('img');
        const simg = slide.querySelector('img');
        const zoom = slide.querySelector('[data-zoom-src]');
        const video = slide.querySelector('.wwa-gallery__video');
        return {
          i,
          video: !!video,
          html: video ? video.innerHTML : '',
          src: zoom ? zoom.dataset.zoomSrc : (simg ? simg.currentSrc || simg.src : ''),
          lo: simg ? simg.currentSrc || simg.src : '',
          alt: simg ? simg.alt : '',
          thumb: timg ? timg.currentSrc || timg.src : (simg ? simg.currentSrc || simg.src : ''),
        };
      });
      this.viewerItems = items;
      const nVideos = items.filter((it) => it.video).length;
      const nImages = items.length - nVideos;
      const counts = [nImages ? `${nImages} image${nImages === 1 ? '' : 's'}` : '', nVideos ? `${nVideos} video${nVideos === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
      dlg.innerHTML = `
        <div class="wwa-viewer__box">
          <div class="wwa-viewer__head">
            <p class="wwa-viewer__title"></p>
            <span class="wwa-viewer__counts">${counts}</span>
            <button type="button" class="wwa-viewer__close" data-wwa-viewer-close aria-label="Close">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
            </button>
          </div>
          <div class="wwa-viewer__body">
            <div class="wwa-viewer__stage">
              <button type="button" class="wwa-viewer__nav wwa-viewer__nav--prev" data-wwa-viewer-step="-1" aria-label="Previous"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><polyline points="15 5 8 12 15 19"/></svg></button>
              <div class="wwa-viewer__media" data-wwa-viewer-media></div>
              <button type="button" class="wwa-viewer__nav wwa-viewer__nav--next" data-wwa-viewer-step="1" aria-label="Next"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><polyline points="9 5 16 12 9 19"/></svg></button>
              <p class="wwa-viewer__pos" data-wwa-viewer-pos></p>
            </div>
            <div class="wwa-viewer__grid" role="list"></div>
          </div>
        </div>`;
      dlg.querySelector('.wwa-viewer__title').textContent = title.trim();
      const grid = dlg.querySelector('.wwa-viewer__grid');
      items.forEach((it) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'wwa-viewer__thumb';
        b.setAttribute('role', 'listitem');
        b.dataset.index = String(it.i);
        b.setAttribute('aria-label', `${it.video ? 'Video' : 'Image'} ${it.i + 1} of ${items.length}`);
        b.innerHTML = `${it.thumb ? `<img src="${it.thumb}" alt="" loading="lazy">` : ''}${it.video ? '<span class="wwa-viewer__play" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg></span>' : ''}`;
        grid.appendChild(b);
      });
      dlg.addEventListener('click', (evt) => {
        if (evt.target === dlg || evt.target.closest('[data-wwa-viewer-close]')) { this.closeViewer(); return; }
        const step = evt.target.closest('[data-wwa-viewer-step]');
        if (step) { this.viewerShow(this.viewerIndex + Number(step.dataset.wwaViewerStep)); return; }
        const t = evt.target.closest('.wwa-viewer__thumb');
        if (t) this.viewerShow(Number(t.dataset.index));
      });
      dlg.addEventListener('keydown', (evt) => {
        if (evt.key === 'ArrowRight') { evt.preventDefault(); this.viewerShow(this.viewerIndex + 1); }
        else if (evt.key === 'ArrowLeft') { evt.preventDefault(); this.viewerShow(this.viewerIndex - 1); }
      });
      // Swipe left/right on phones.
      let sx = null;
      const stage = dlg.querySelector('.wwa-viewer__stage');
      stage.addEventListener('touchstart', (evt) => { sx = evt.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (evt) => {
        if (sx === null) return;
        const dx = evt.changedTouches[0].clientX - sx;
        sx = null;
        if (Math.abs(dx) > 50) this.viewerShow(this.viewerIndex + (dx < 0 ? 1 : -1));
      }, { passive: true });
      dlg.addEventListener('close', () => {
        const media = dlg.querySelector('[data-wwa-viewer-media]');
        if (media) media.innerHTML = '';
        document.documentElement.classList.remove('wwa-viewer-open');
      });
      this.appendChild(dlg);
      this.viewer = dlg;
    }

    /** @param {number} index Item to show (wraps around). */
    viewerShow(index) {
      const items = this.viewerItems || [];
      if (!items.length) return;
      const i = (index + items.length) % items.length;
      this.viewerIndex = i;
      const it = items[i];
      const media = this.viewer.querySelector('[data-wwa-viewer-media]');
      if (it.video) {
        media.innerHTML = `<div class="wwa-viewer__video">${it.html}</div>`;
        const v = media.querySelector('video');
        if (v) { v.controls = true; v.preload = 'auto'; }
        const f = media.querySelector('iframe');
        if (f) f.setAttribute('loading', 'eager');
      } else {
        media.innerHTML = '';
        // Show the photo already on the page right away, then swap in the sharper one.
        const img = document.createElement('img');
        img.src = it.lo || it.src;
        img.alt = it.alt;
        media.appendChild(img);
        if (it.src && it.src !== img.src) {
          const hi = new Image();
          hi.onload = () => { if (this.viewerIndex === i && img.isConnected) img.src = it.src; };
          hi.src = it.src;
        }
      }
      this.viewer.querySelector('[data-wwa-viewer-pos]').textContent = `${i + 1} / ${items.length}`;
      this.viewer.querySelectorAll('.wwa-viewer__thumb').forEach((t) => {
        const on = Number(t.dataset.index) === i;
        t.classList.toggle('is-active', on);
        if (on) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } else t.removeAttribute('aria-current');
      });
      document.documentElement.classList.add('wwa-viewer-open');
    }

    closeViewer() {
      if (!this.viewer) return;
      if (typeof this.viewer.close === 'function' && this.viewer.open) this.viewer.close();
      else this.viewer.removeAttribute('open');
    }

    /** Opens the phone's share sheet, or copies the link. */
    async share(btn) {
      const data = { title: document.title, url: window.location.href.split('?')[0] };
      try {
        if (navigator.share) { await navigator.share(data); return; }
        await navigator.clipboard.writeText(data.url);
        btn.classList.add('is-copied');
        setTimeout(() => btn.classList.remove('is-copied'), 1500);
      } catch (e) { /* share cancelled */ }
    }

    /**
     * Shows one media item and marks its thumbnail.
     * @param {string} mediaId Shopify media id.
     */
    show(mediaId) {
      if (!mediaId) return;
      const target = this.mark(mediaId);
      if (!target) return;
      // Phone carousel: slide to the chosen image.
      if (mobileMq.matches && this.stage) {
        this.stage.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }
      const img = target.querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }

    /**
     * Marks one media item as active (slide, thumbnail and dot) without scrolling.
     * @param {string} mediaId Shopify media id.
     * @returns {?HTMLElement} The active slide.
     */
    mark(mediaId) {
      const target = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!target || target.classList.contains('is-active')) return target || null;

      this.slides.forEach((slide) => {
        const active = slide === target;
        slide.classList.toggle('is-active', active);
        if (!active) {
          const video = slide.querySelector('video');
          if (video) video.pause();
        }
      });
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.dot === target.dataset.index));
      return target;
    }

    /**
     * Hover zoom: scales the image and follows the cursor.
     * @param {HTMLElement} zoom The .wwa-gallery__zoom wrapper.
     */
    bindZoom(zoom) {
      const img = zoom.querySelector('img');
      if (!img) return;
      let hiResLoaded = false;

      zoom.addEventListener('mouseenter', () => {
        if (!hiResLoaded && zoom.dataset.zoomSrc) {
          img.srcset = '';
          img.src = zoom.dataset.zoomSrc;
          hiResLoaded = true;
        }
        zoom.classList.add('is-zooming');
      });
      zoom.addEventListener('mousemove', (evt) => {
        const rect = zoom.getBoundingClientRect();
        const x = ((evt.clientX - rect.left) / rect.width) * 100;
        const y = ((evt.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      });
      zoom.addEventListener('mouseleave', () => {
        zoom.classList.remove('is-zooming');
        img.style.transformOrigin = '';
      });
    }
  }

  /* ------------------------------------------------------------------------
     <wwa-product>
     ------------------------------------------------------------------------ */
  class WwaProduct extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat;
      this.priceFormat = this.dataset.moneyCurrencyFormat || this.dataset.moneyFormat;
      this.lowStock = parseInt(this.dataset.lowStock, 10) || 0;
      this.form = this.querySelector(`#wwa-form-${this.dataset.sectionId}`);
      this.idInput = this.querySelector('[data-wwa-id]');
      this.qtySelect = this.querySelector('[data-wwa-qty]');
      this.atcBtn = this.querySelector('[data-wwa-atc]');
      this.buyBtn = this.querySelector('[data-wwa-buy]');
      this.errorEl = this.querySelector('[data-wwa-error]');
      this.gallery = this.querySelector('wwa-gallery');
      this.variantBtns = Array.from(this.querySelectorAll('[data-variant-id]'));

      this.variantBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.selectVariant(btn));
      });

      if (this.form) {
        this.form.addEventListener('submit', (evt) => {
          evt.preventDefault();
          this.submit(false);
        });
      }
      if (this.buyBtn) this.buyBtn.addEventListener('click', () => this.submit(true));

      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.initOptions();
      this.initXAddons();
      this.initFbt();
      this.initBundles();
      this.initSticky();
      this.initAbout();
      // The Q&A section renders after this one, so wait for the page.
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.initPills());
      else this.initPills();
    }

    /**
     * Products with several options: one button row per option plus add-on checkboxes.
     * Picks the matching variant from the hidden flat list and shows price differences.
     */
    initOptions() {
      this.ogroups = this.querySelector('[data-wwa-ogroups]');
      if (!this.ogroups) return;
      this.variants = this.variantBtns.map((btn) => {
        let opts = [];
        try { opts = JSON.parse(btn.dataset.options || '[]'); } catch (e) { opts = []; }
        return { btn, opts, price: parseInt(btn.dataset.price, 10) || 0, avail: btn.dataset.available === 'true' };
      });
      const current = this.variants.find((v) => v.btn.classList.contains('is-selected')) || this.variants[0];
      if (!current) return;
      this.sel = current.opts.slice();

      this.ogroups.addEventListener('click', (evt) => {
        const b = evt.target.closest('[data-wwa-obtn]');
        if (!b) return;
        this.setOption(parseInt(b.dataset.optIndex, 10), b.dataset.value);
      });
      // Add-on checkboxes: the plain ones inside the option groups and the picture cards below them.
      this.addEventListener('change', (evt) => {
        const c = evt.target.closest('[data-wwa-addon]');
        if (!c) return;
        this.setOption(parseInt(c.dataset.optIndex, 10), c.checked ? c.dataset.on : c.dataset.off);
      });
      // Options replaced by separate add-on products stay on "Skip".
      this.forced = [];
      try { this.forced = JSON.parse(this.ogroups.dataset.force || '[]'); } catch (e) { this.forced = []; }
      if (this.forced.length) {
        const want = this.applyForced(this.sel.slice());
        const match = this.findVariant(want);
        if (match && match !== current) {
          this.sel = match.opts.slice();
          this.selectVariant(match.btn);
        }
      }
      this.refreshOptions();
    }

    /** @param {string[]} sel @returns {string[]} sel with forced values applied. */
    applyForced(sel) {
      (this.forced || []).forEach((f) => { sel[f.i] = f.v; });
      return sel;
    }

    /* ---------- Bundle add-ons sold as separate products ---------- */
    initXAddons() {
      this.xAddons = Array.from(this.querySelectorAll('[data-wwa-xaddon]'));
      this.xAddons.forEach((input) => input.addEventListener('change', () => {
        this.querySelectorAll('[data-wwa-price]').forEach((el) => {
          const s = formatMoney(this.basePrice + this.xAddonTotal(), this.priceFormat); if (window.wwMoneyHTML) el.innerHTML = window.wwMoneyHTML(s); else el.textContent = s;
        });
      }));
    }

    /** @returns {number} Cents for the checked add-ons, at their bundle price. */
    xAddonTotal() {
      return (this.xAddons || []).filter((i) => i.checked && !i.disabled)
        .reduce((sum, i) => sum + (parseInt(i.dataset.price, 10) || 0), 0);
    }

    /** @param {string[]} sel Option values. @returns {?object} The exact variant. */
    findVariant(sel) {
      return this.variants.find((v) => v.opts.every((o, i) => o === sel[i])) || null;
    }

    /**
     * @param {number} index Option position.
     * @param {string} value Chosen value.
     */
    setOption(index, value) {
      const want = this.sel.slice();
      want[index] = value;
      this.applyForced(want);
      let match = this.findVariant(want);
      if (!match) {
        // No exact combination: keep the new value and as many other choices as possible.
        let best = null;
        let bestScore = -1;
        this.variants.forEach((v) => {
          if (v.opts[index] !== value) return;
          const score = v.opts.reduce((n, o, i) => n + (o === want[i] ? 1 : 0), 0) + (v.avail ? 0.5 : 0);
          if (score > bestScore) { best = v; bestScore = score; }
        });
        match = best;
      }
      if (!match) return;
      this.sel = match.opts.slice();
      this.selectVariant(match.btn);
      this.refreshOptions();
    }

    /** Marks the chosen buttons and writes +$/-$ differences against the current choice. */
    refreshOptions() {
      const current = this.findVariant(this.sel);
      if (!current) return;
      const diff = (index, value) => {
        const alt = this.sel.slice();
        alt[index] = value;
        const v = this.findVariant(alt);
        return v ? { delta: v.price - current.price, avail: v.avail } : null;
      };
      const fmt = (cents) => `${cents > 0 ? '+' : '−'}${formatMoney(Math.abs(cents), this.moneyFormat)}`;

      this.ogroups.querySelectorAll('[data-wwa-obtn]').forEach((b) => {
        const i = parseInt(b.dataset.optIndex, 10);
        const on = this.sel[i] === b.dataset.value;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        const d = on ? null : diff(i, b.dataset.value);
        const out = b.querySelector('[data-wwa-delta]');
        if (out) out.textContent = d && d.delta ? fmt(d.delta) : '';
        b.classList.toggle('is-unavailable', !on && (!d || !d.avail));
      });
      this.ogroups.querySelectorAll('[data-wwa-oval]').forEach((el) => {
        el.textContent = this.sel[parseInt(el.dataset.wwaOval, 10)] || '';
      });
      this.querySelectorAll('[data-wwa-addon]').forEach((c) => {
        const i = parseInt(c.dataset.optIndex, 10);
        c.checked = this.sel[i] === c.dataset.on;
        const d = diff(i, c.dataset.on);
        const off = diff(i, c.dataset.off);
        const out = c.parentElement.querySelector('[data-wwa-delta]');
        if (out) {
          const extra = d && off ? d.delta - off.delta : 0;
          out.textContent = extra > 0 ? `+${formatMoney(extra, this.moneyFormat)}` : '';
        }
        c.parentElement.classList.toggle('is-unavailable', !d || !d.avail);
      });
    }

    /** Phone: question chips under the photos, taken from the Q&A section, like Amazon's. */
    initPills() {
      const wrap = this.querySelector('[data-wwa-pills]');
      if (!wrap || wrap.children.length) return;
      const links = [];
      document.querySelectorAll('#wwa-qa .wwa-qa__item dt').forEach((dt, i) => {
        if (i > 2) return;
        const text = dt.textContent.replace(/^\s*Q:\s*/, '').trim();
        if (text) links.push([text, '#wwa-qa']);
      });
      if (document.getElementById('wwa-compare')) links.push(['Compare similar items', '#wwa-compare']);
      if (document.getElementById('wwa-reviews')) links.push(['Read reviews', '#wwa-reviews']);
      links.forEach(([text, href]) => {
        const a = document.createElement('a');
        a.className = 'wwa-mpill';
        a.href = href;
        a.textContent = text;
        wrap.appendChild(a);
      });
      wrap.hidden = links.length === 0;
    }

    /** Long "About this item" lists fold behind a soft fade and a "See more" button. */
    initAbout() {
      const about = this.querySelector('.wwa-about');
      const toggle = about && about.querySelector('[data-wwa-about-toggle]');
      const list = about && about.querySelector('.wwa-bullets');
      if (!toggle || !list) return;
      const label = toggle.querySelector('span') || toggle;
      const measure = () => {
        if (about.dataset.open === 'true') return;
        about.classList.add('is-clamped');
        const long = list.scrollHeight > list.clientHeight + 40;
        about.classList.toggle('is-clamped', long);
        toggle.hidden = !long;
      };
      measure();
      window.addEventListener('resize', measure, { passive: true });
      toggle.addEventListener('click', () => {
        const open = about.classList.contains('is-clamped');
        about.classList.toggle('is-clamped', !open);
        about.dataset.open = open ? 'true' : 'false';
        label.textContent = open ? ((window.wwT && wwT.see_less) || 'See less') : ((window.wwT && wwT.see_more) || 'See more');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        // Folding back up: keep the list in view instead of leaving the reader far below it.
        if (!open && about.getBoundingClientRect().top < 0) about.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }

    /**
     * Applies a variant chosen with the size buttons.
     * @param {HTMLElement} btn The clicked variant button.
     */
    selectVariant(btn) {
      const d = btn.dataset;
      const price = parseInt(d.price, 10) || 0;
      const compare = parseInt(d.compare, 10) || 0;
      const available = d.available === 'true';

      this.variantBtns.forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      if (this.idInput) this.idInput.value = d.variantId;

      this.basePrice = price;
      // Product-family tile for this page: show the chosen variant's own price and stock.
      this.querySelectorAll('[data-wwa-sibprice]').forEach((el) => { el.textContent = formatMoney(price, this.moneyFormat); });
      this.querySelectorAll('[data-wwa-sibwas]').forEach((el) => { el.textContent = formatMoney(compare, this.moneyFormat); el.hidden = !(compare > price); });
      this.querySelectorAll('[data-wwa-sibstock]').forEach((el) => {
        el.textContent = available ? el.dataset.in : el.dataset.out;
        el.classList.toggle('is-out', !available);
      });
      this.querySelectorAll('[data-wwa-price]').forEach((el) => { const s = formatMoney(price + this.xAddonTotal(), this.priceFormat); if (window.wwMoneyHTML) el.innerHTML = window.wwMoneyHTML(s); else el.textContent = s; });

      const off = this.querySelector('[data-wwa-off]');
      const wasRow = this.querySelector('[data-wwa-was-row]');
      const was = this.querySelector('[data-wwa-was]');
      if (compare > price) {
        if (off) { off.textContent = `-${Math.floor(((compare - price) * 100) / compare)}%`; off.hidden = false; }
        if (was) was.textContent = formatMoney(compare, this.moneyFormat);
        if (wasRow) wasRow.hidden = false;
      } else {
        if (off) off.hidden = true;
        if (wasRow) wasRow.hidden = true;
      }

      const sub = this.querySelector('[data-wwa-sub]');
      if (sub) {
        const subPrice = parseInt(d.sub, 10) || 0;
        const pct = subPrice > 0 && subPrice < price ? Math.round(((price - subPrice) * 100) / price) : 0;
        sub.hidden = pct === 0;
        const sp = sub.querySelector('[data-wwa-sub-price]');
        if (sp) sp.textContent = formatMoney(subPrice, this.moneyFormat);
        const pe = sub.querySelector('[data-wwa-sub-pct]');
        if (pe) pe.textContent = pct ? ` (save ${pct}%)` : '';
      }

      const unit = this.querySelector('[data-wwa-unit]');
      if (unit) { unit.textContent = d.unit || ''; unit.hidden = !d.unit; }

      const title = this.querySelector('[data-wwa-variant-title]');
      if (title) title.textContent = d.title;

      this.updateStock(available, d.inventory === '' ? null : parseInt(d.inventory, 10));
      [this.atcBtn, this.buyBtn].forEach((b) => { if (b) b.disabled = !available; });
      const bis = this.querySelector('[data-wwa-bis]');
      if (bis) bis.hidden = available;

      if (d.mediaId && this.gallery) this.gallery.show(d.mediaId);

      const fbtMain = this.querySelector('[data-wwa-fbt-main]');
      const fbtMainPrice = this.querySelector('[data-wwa-fbt-main-price]');
      if (fbtMain) fbtMain.dataset.price = String(price);
      if (fbtMainPrice) fbtMainPrice.textContent = formatMoney(price, this.moneyFormat);
      this.updateFbtTotal();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', d.variantId);
      window.history.replaceState({}, '', url.toString());

      // Let subscription apps and the theme know the variant changed.
      document.dispatchEvent(new CustomEvent('on:variant:change', {
        detail: { variant: { id: Number(d.variantId), price, compare_at_price: compare, available }, source: 'wwa' },
      }));
      if (this.idInput) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * @param {boolean} available Whether the variant can be bought.
     * @param {?number} inventory Units left when Shopify tracks stock, else null.
     */
    updateStock(available, inventory) {
      const el = this.querySelector('[data-wwa-stock]');
      if (!el) return;
      el.classList.remove('is-low', 'is-out');
      if (!available) {
        el.textContent = this.dataset.outStockText;
        el.classList.add('is-out');
      } else if (inventory !== null && this.lowStock > 0 && inventory <= this.lowStock) {
        el.textContent = (this.dataset.lowStockText || '').replace('[count]', inventory);
        el.classList.add('is-low');
      } else {
        el.textContent = this.dataset.inStockText;
      }
    }

    /** @returns {number} Selected quantity. */
    getQty() {
      const q = this.qtySelect ? parseInt(this.qtySelect.value, 10) : 1;
      return Number.isNaN(q) || q < 1 ? 1 : q;
    }

    /**
     * Builds the main line item, carrying a subscription plan if an app set one.
     * @returns {object} Cart item.
     */
    mainItem() {
      const item = { id: Number(this.idInput ? this.idInput.value : 0), quantity: this.getQty() };
      if (this.form) {
        const plan = new FormData(this.form).get('selling_plan');
        if (plan) item.selling_plan = Number(plan);
      }
      return item;
    }

    /**
     * Add to Cart, or Buy Now when checkout is true.
     * @param {boolean} checkout Go straight to checkout after adding.
     */
    async submit(checkout) {
      const btn = checkout ? this.buyBtn : this.atcBtn;
      if (!btn || btn.disabled) return;
      this.hideError(this.errorEl);
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');

      try {
        const main = this.mainItem();
        const extras = (this.xAddons || []).filter((i) => i.checked && !i.disabled)
          .map((i) => ({ id: Number(i.value), quantity: main.quantity || 1 }));
        const data = await addToCart([main, ...extras]);
        if (checkout) {
          window.location.href = '/checkout';
          return;
        }
        refreshCartUi(data);
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1500);
      } catch (error) {
        this.showError(this.errorEl, error.message);
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }

    /* ---------- Frequently bought together ---------- */
    initFbt() {
      this.fbt = this.querySelector('[data-wwa-fbt]');
      if (!this.fbt) return;
      this.fbtAddons = Array.from(this.fbt.querySelectorAll('[data-wwa-fbt-addon]'));
      this.fbtAddons.forEach((input) => input.addEventListener('change', () => this.updateFbtTotal()));
      const btn = this.fbt.querySelector('[data-wwa-fbt-add]');
      if (btn) btn.addEventListener('click', () => this.addFbt(btn));
      this.updateFbtTotal();
    }

    /** True when every available add-on is checked, so the bundle discount applies. */
    fbtAllChecked() {
      const available = this.fbtAddons.filter((i) => !i.disabled);
      return available.length > 0 && available.every((i) => i.checked);
    }

    updateFbtTotal() {
      if (!this.fbt) return;
      const main = this.fbt.querySelector('[data-wwa-fbt-main]');
      const checked = this.fbtAddons.filter((i) => i.checked);

      // Hide the picture (and its "+") of every item that isn't checked.
      this.fbtAddons.forEach((input) => {
        const part = this.fbt.querySelector(`[data-wwa-fbt-part="${input.value}"]`);
        if (part) part.hidden = !input.checked;
      });

      const total = checked.reduce((sum, i) => sum + (parseInt(i.dataset.price, 10) || 0),
        main ? parseInt(main.dataset.price, 10) || 0 : 0);
      const percent = parseFloat(this.fbt.dataset.discountPercent) || 0;
      const deal = percent > 0 && this.fbtAllChecked();
      const saving = deal ? Math.round(total * percent / 100) : 0;

      const totalEl = this.fbt.querySelector('[data-wwa-fbt-total]');
      if (totalEl) totalEl.textContent = formatMoney(total - saving, this.moneyFormat);
      const wasEl = this.fbt.querySelector('[data-wwa-fbt-was]');
      if (wasEl) {
        wasEl.hidden = !deal;
        wasEl.textContent = formatMoney(total, this.moneyFormat);
      }
      const dealOn = this.fbt.querySelector('[data-wwa-fbt-deal-on]');
      const dealOff = this.fbt.querySelector('[data-wwa-fbt-deal-off]');
      if (dealOn) dealOn.hidden = !deal;
      if (dealOff) dealOff.hidden = deal;
      const saveEl = this.fbt.querySelector('[data-wwa-fbt-save]');
      if (saveEl) saveEl.textContent = formatMoney(saving, this.moneyFormat);

      const label = this.fbt.querySelector('[data-wwa-fbt-btn-text]');
      if (label) label.textContent = (label.dataset.template || '').replace('[count]', checked.length + 1);
    }

    /**
     * Adds the bundle discount code to the cart, keeping any codes already there.
     * @param {string} code Discount code.
     * @returns {Promise<object|null>} Updated cart with rendered sections, or null.
     */
    async applyFbtDiscount(code) {
      try {
        const cart = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
        if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);

        const drawer = getCartDrawer();
        const sections = ['cart-icon-bubble'];
        const drawerSection = drawer && drawer.closest('.shopify-section');
        if (drawerSection) sections.push(drawerSection.id.replace('shopify-section-', ''));

        const response = await fetch(`${routes.cart}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: sections.join(','), sections_url: window.location.pathname }),
        });
        if (!response.ok) throw new Error('update failed');
        return await response.json();
      } catch (e) {
        // Older cart API: this sets the code for checkout instead.
        try { await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }); } catch (err) { /* ignore */ }
        return null;
      }
    }

    /** @param {HTMLElement} btn The FBT add button. */
    async addFbt(btn) {
      const errorEl = this.fbt.querySelector('[data-wwa-fbt-error]');
      this.hideError(errorEl);
      const main = this.mainItem();
      main.quantity = 1;
      const items = [main];
      this.fbtAddons.filter((i) => i.checked).forEach((i) => items.push({ id: Number(i.value), quantity: 1 }));

      const percent = parseFloat(this.fbt.dataset.discountPercent) || 0;
      const code = (this.fbt.dataset.discountCode || '').trim();
      const deal = percent > 0 && code && this.fbtAllChecked();

      btn.classList.add('is-loading');
      try {
        let data = await addToCart(items);
        if (deal) {
          const updated = await this.applyFbtDiscount(code);
          if (updated && updated.sections) data = updated;
        }
        refreshCartUi(data);
      } catch (error) {
        this.showError(errorEl, error.message);
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    /* ---------- Kits & bundles with this item ---------- */
    initBundles() {
      const wrap = this.querySelector('[data-wwa-bundles]');
      if (!wrap) return;
      const errorEl = wrap.querySelector('[data-wwa-bundle-error]');
      wrap.querySelectorAll('[data-wwa-bundle-add]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          this.hideError(errorEl);
          btn.classList.add('is-loading');
          try {
            const data = await addToCart([{ id: Number(btn.dataset.wwaBundleAdd), quantity: 1 }]);
            refreshCartUi(data);
          } catch (error) {
            this.showError(errorEl, error.message);
          } finally {
            btn.classList.remove('is-loading');
          }
        });
      });
    }

    /* ---------- Mobile sticky bar ---------- */
    initSticky() {
      const bar = this.querySelector('[data-wwa-sticky]');
      if (!bar) return;
      const watched = document.querySelector(bar.dataset.watch);
      if (!watched) return;
      const stickyBtn = bar.querySelector('[data-wwa-sticky-atc]');

      // Show the bar once the buy box has scrolled up out of view.
      let ticking = false;
      const update = () => {
        ticking = false;
        const show = watched.getBoundingClientRect().bottom < 0;
        if (show === bar.classList.contains('is-visible')) return;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      }, { passive: true });
      update();

      if (stickyBtn) stickyBtn.addEventListener('click', () => this.submit(false));
    }

    showError(el, message) {
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    }

    hideError(el) {
      if (el) el.hidden = true;
    }
  }

  /* ------------------------------------------------------------------------
     <wwa-recs> — product recommendations row
     ------------------------------------------------------------------------ */
  class WwaRecs extends HTMLElement {
    connectedCallback() {
      this.row = this.querySelector('.wwa-row');
      this.bindArrows();
      if (this.row && this.row.children.length === 0 && this.dataset.url) {
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) { io.disconnect(); this.load(); }
          }, { rootMargin: '400px 0px' });
          io.observe(this);
        } else {
          this.load();
        }
      }
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-wwa-recs-target="${this.dataset.blockId}"]`);
        if (fresh && fresh.children.length) {
          this.row.innerHTML = fresh.innerHTML;
          this.checkOverflow();
        } else {
          const sec = this.closest('.wwa-sec');
          if (sec) sec.hidden = true;
        }
      } catch (e) {
        const sec = this.closest('.wwa-sec');
        if (sec) sec.hidden = true;
      }
    }

    bindArrows() {
      const prev = this.querySelector('[data-wwa-row-prev]');
      const next = this.querySelector('[data-wwa-row-next]');
      const step = () => Math.max(this.row.clientWidth * 0.8, 200);
      if (prev) prev.addEventListener('click', () => this.row.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => this.row.scrollBy({ left: step(), behavior: 'smooth' }));
      window.addEventListener('resize', () => this.checkOverflow());
      this.checkOverflow();
    }

    checkOverflow() {
      if (!this.row) return;
      this.classList.toggle('has-overflow', this.row.scrollWidth > this.row.clientWidth + 4);
    }
  }

  /* ------------------------------------------------------------------------
     Phone: below-the-fold sections marked data-wwa-acc fold into accordions,
     like Amazon's "Product specifications" and "About the brand".
     ------------------------------------------------------------------------ */
  function initAccordions(root) {
    root.querySelectorAll('.wwa-sec[data-wwa-acc]').forEach((sec) => {
      if (sec.dataset.accReady) return;
      const head = sec.querySelector(':scope > .wwa-h2');
      if (!head) return;
      sec.dataset.accReady = 'true';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wwa-acc__btn';
      btn.setAttribute('aria-expanded', 'false');
      btn.innerHTML = '<span></span><svg class="wwa-acc__chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
      btn.firstChild.textContent = head.textContent.trim();
      head.textContent = '';
      head.appendChild(btn);
      sec.classList.add('wwa-acc');
      btn.addEventListener('click', () => {
        const open = sec.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      // Jump links (question chips, "See more") open their section.
      if (sec.id && window.location.hash === `#${sec.id}`) sec.classList.add('is-open');
    });
  }
  document.addEventListener('click', (evt) => {
    const link = evt.target.closest('a[href^="#wwa-"]');
    if (!link) return;
    const sec = document.querySelector(link.getAttribute('href'));
    if (sec && sec.classList.contains('wwa-acc')) {
      sec.classList.add('is-open');
      const btn = sec.querySelector('.wwa-acc__btn');
      if (btn) btn.setAttribute('aria-expanded', 'true');
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAccordions(document));
  else initAccordions(document);
  document.addEventListener('shopify:section:load', (evt) => initAccordions(evt.target));

  // Long review text: clamp to a few lines with a Read more toggle.
  function initClamps(root) {
    root.querySelectorAll('[data-wwa-clamp]').forEach((el) => {
      if (el.dataset.wwaClampReady) return;
      el.dataset.wwaClampReady = '1';
      if (el.scrollHeight <= el.clientHeight + 4) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wwa-rev__more';
      btn.textContent = (window.wwT && wwT.read_more) || 'Read more';
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', () => {
        const open = el.classList.toggle('is-open');
        btn.textContent = open ? ((window.wwT && wwT.read_less) || 'Read less') : ((window.wwT && wwT.read_more) || 'Read more');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      el.after(btn);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initClamps(document));
  else initClamps(document);
  document.addEventListener('shopify:section:load', (evt) => initClamps(evt.target));

  // "Choose a subscription plan" unfolds the subscription widget (it stays hidden until then).
  document.addEventListener('click', (evt) => {
    const btn = evt.target.closest && evt.target.closest('[data-wwa-sub-link]');
    if (!btn) return;
    const root = btn.closest('.wwa');
    if (!root) return;
    evt.preventDefault();
    const open = !root.classList.contains('is-sub-open');
    root.classList.toggle('is-sub-open', open);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    const o = btn.querySelector('[data-wwa-sub-open]');
    const c = btn.querySelector('[data-wwa-sub-close]');
    if (o) o.hidden = open;
    if (c) c.hidden = !open;
    if (!open) {
      // Folding the plans away goes back to a one-time purchase, so nothing hidden stays selected.
      const app = root.querySelector('[id*="appstle"]');
      const box = app && (app.closest('.wwa-buybox__app') || app);
      const radios = box ? Array.from(box.querySelectorAll('input[type="radio"]')) : [];
      if (radios.length > 1 && !radios[0].checked) radios[0].click();
      return;
    }
    const target = root.querySelector('[id*="appstle"]');
    if (!target) return;
    const box = (target.closest('.wwa-buybox__app') || target).getBoundingClientRect();
    // Bring the plans into view only if they're below the fold.
    if (box.bottom > window.innerHeight - 20) {
      window.scrollBy({ top: box.bottom - window.innerHeight + 90, behavior: 'smooth' });
    }
  });

  /* ---------- Switch sizes without a full page reload ----------
     Sizes that are separate products link to each other. Instead of loading
     a whole new page, fetch the product sections for the new URL and swap
     them in place, so only the photos and details change. Falls back to a
     normal page load if anything is missing. */
  const swapCache = new Map();
  const swapSections = () => Array.from(document.querySelectorAll('[id^="shopify-section-template--"]'))
    .filter((sec) => sec.querySelector('.wwa'));
  const swapKey = (sec) => sec.id.replace('shopify-section-', '');

  function fetchSwap(url) {
    const ids = swapSections().map(swapKey).slice(0, 5);
    if (!ids.length) return Promise.reject(new Error('no sections'));
    const u = new URL(url, window.location.href);
    u.searchParams.set('sections', ids.join(','));
    const key = u.toString();
    if (!swapCache.has(key)) {
      swapCache.set(key, fetch(key, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .catch((err) => { swapCache.delete(key); throw err; }));
    }
    return swapCache.get(key).then((data) => ({ ids, data }));
  }

  function runInlineScripts(root) {
    root.querySelectorAll('script').forEach((old) => {
      const type = (old.getAttribute('type') || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'module' && type !== 'application/javascript') return;
      if (old.src && /ww-amz\.js/.test(old.src)) return;
      const fresh = document.createElement('script');
      Array.from(old.attributes).forEach((a) => fresh.setAttribute(a.name, a.value));
      fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  }

  let swapBusy = false;
  async function swapProduct(url, push) {
    if (swapBusy) return;
    swapBusy = true;
    const secs = swapSections();
    secs.forEach((sec) => { sec.style.transition = 'opacity .15s'; sec.style.opacity = '0.55'; });
    try {
      const { ids, data } = await fetchSwap(url);
      if (ids.some((id) => !data[id])) throw new Error('missing section');
      const oldList = document.querySelector('.wwa-options__list');
      const keepScroll = oldList ? oldList.scrollLeft : 0;
      // Put the new product's variant in the address, so subscription apps don't carry the old one over.
      const target = new URL(url, window.location.href);
      if (!target.searchParams.has('variant')) {
        const m = ids.map((id) => data[id]).join('').match(/data-wwa-id[^>]*value="(\d+)"|value="(\d+)"[^>]*data-wwa-id/);
        const vid = m && (m[1] || m[2]);
        if (vid) target.searchParams.set('variant', vid);
      }
      if (push) window.history.pushState({ wwaSwap: true }, '', target.toString());
      else if (target.toString() !== window.location.href) window.history.replaceState({ wwaSwap: true }, '', target.toString());
      // Lower sections first, so the top section reads the new Q&A when it starts.
      ids.slice().reverse().forEach((id) => {
        const current = document.getElementById(`shopify-section-${id}`);
        const holder = document.createElement('div');
        holder.innerHTML = data[id];
        const next = holder.querySelector(`#shopify-section-${id}`) || holder.firstElementChild;
        if (!current || !next) return;
        current.replaceWith(next);
        runInlineScripts(next);
        next.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id, source: 'wwa-swap' } }));
      });
      const newList = document.querySelector('.wwa-options__list');
      if (newList) newList.scrollLeft = keepScroll;
      const h1 = document.querySelector('.wwa-title');
      if (h1) {
        const parts = document.title.split(' | ');
        document.title = parts.length > 1 ? `${h1.textContent.trim()} | ${parts[parts.length - 1]}` : h1.textContent.trim();
      }
      // Klaviyo reviews only draw on page load; ask them to draw again for the new product.
      try {
        const snippet = document.getElementById('kl_reviews_rich_snippet');
        if (snippet) snippet.remove();
        if (window.klReviewsObject && typeof window.klReviewsObject.renderWidgets === 'function') window.klReviewsObject.renderWidgets();
      } catch (e) { /* reviews app changed */ }
      document.dispatchEvent(new CustomEvent('wwa:product:swapped', { detail: { url } }));
    } catch (err) {
      window.location.href = url;
      return;
    } finally {
      swapBusy = false;
      swapSections().forEach((sec) => { sec.style.opacity = ''; });
    }
  }

  const isSwapLink = (a) => a && a.matches('.wwa a.wwa-option[href]') && !a.hasAttribute('aria-current')
    && new URL(a.href, window.location.href).origin === window.location.origin;

  document.addEventListener('click', (evt) => {
    const a = evt.target.closest && evt.target.closest('a.wwa-option[href]');
    if (!isSwapLink(a) || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button !== 0) return;
    evt.preventDefault();
    swapProduct(a.href, true);
  });
  // Start loading as soon as a finger or mouse lands on a size, so the swap feels instant.
  ['pointerenter', 'touchstart', 'focusin'].forEach((type) => {
    document.addEventListener(type, (evt) => {
      const a = evt.target.closest && evt.target.closest('a.wwa-option[href]');
      if (isSwapLink(a)) fetchSwap(a.href).catch(() => {});
    }, { capture: true, passive: true });
  });
  let swapPath = window.location.pathname;
  document.addEventListener('wwa:product:swapped', () => { swapPath = window.location.pathname; });
  window.addEventListener('popstate', () => {
    if (window.location.pathname === swapPath) return;
    swapPath = window.location.pathname;
    if (document.querySelector('.wwa')) swapProduct(window.location.href, false);
  });
  if (!window.history.state) window.history.replaceState({ wwaSwap: true }, '', window.location.href);

  if (!customElements.get('wwa-gallery')) customElements.define('wwa-gallery', WwaGallery);
  if (!customElements.get('wwa-product')) customElements.define('wwa-product', WwaProduct);
  if (!customElements.get('wwa-recs')) customElements.define('wwa-recs', WwaRecs);
})();
